<template>
  <div class="scroll-wrapper employees-page">
    <div class="container-fluid">
      <h3 class="pt-2">Список сотрудников</h3>
      <div class="row">
        <div class="employees" style="margin-bottom: 5rem;">
          <div class="col-12 col-lg-10 offset-lg-1 mb-3">
            <div class="d-flex justify-content-between align-items-center employees-toolbar">
              <el-button type="primary" @click="openAddForm">Добавить сотрудника</el-button>
              <el-button :icon="Setting" @click="showRolesManager = true">Должности</el-button>
            </div>
          </div>
          <div class="col-12 col-lg-10 offset-lg-1 employees-panel">
            <div class="table-responsive">
              <el-table
                :data="paginatedUsers"
                stripe
                style="width: 100%; min-width: 768px"
              >
                <el-table-column label="" width="60" align="center">
                  <template #default="scope">
                    <el-avatar
                      :size="32"
                      :src="scope.row.avatar">
                      {{ getInitials(scope.row.name) }}
                    </el-avatar>
                  </template>
                </el-table-column>
                <el-table-column prop="name" label="ФИО" min-width="180" />
                <el-table-column prop="email" label="Email" min-width="260">
                  <template #default="scope">
                    <el-link type="primary" :href="`mailto:${scope.row.email}`">
                      <span class="nowrap-ellipsis">{{ scope.row.email }}</span>
                    </el-link>
                  </template>
                </el-table-column>
                <el-table-column prop="phone" label="Телефон" min-width="170">
                  <template #default="scope">
                    <el-link v-if="scope.row.phone" type="primary" :href="`tel:+${scope.row.phone}`">
                      {{ formatPhone(scope.row.phone) }}
                    </el-link>
                    <span v-else>—</span>
                  </template>
                </el-table-column>
                <el-table-column prop="role.name" label="Роль" min-width="180">
                  <template #default="scope">
                    {{ scope.row.role?.name || 'Без должности' }}
                  </template>
                </el-table-column>
                <el-table-column label="Действия" width="120">
                  <template #default="scope">
                    <el-button :icon="Edit" size="small" circle @click="editEmployee(scope.row)" />
                    <el-button
                      :icon="Delete"
                      type="danger"
                      size="small"
                      circle
                      @click="deleteEmployee(scope.row)"
                      :disabled="scope.row.is_admin"
                    />
                  </template>
                </el-table-column>
              </el-table>
            </div>
            <el-pagination
              background
              layout="prev, pager, next"
              :total="users.length"
              :page-size="pageSize"
              :current-page="currentPage"
              @current-change="handlePageChange"
              class="pagination"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Диалог добавления/редактирования -->
  <el-dialog
    v-model="showForm"
    :title="editMode ? 'Редактировать сотрудника' : 'Добавить сотрудника'"
    class="employees-dialog"
    align-center
    @close="resetForm"
  >
    <el-form :model="newEmployee" ref="employeeForm" label-width="auto" :rules="rules">
      <el-form-item label="Имя" prop="name">
        <el-input v-model="newEmployee.name" autocomplete="off" />
      </el-form-item>
      <el-form-item label="Должность" prop="role_id">
        <el-select v-model="newEmployee.role_id" placeholder="Выберите должность" :disabled="newEmployee.is_admin">
          <el-option
            v-for="role in allRoles"
            :key="role.id"
            :label="role.name"
            :value="role.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="Email" prop="email">
        <el-input v-model="newEmployee.email" :disabled="editMode" autocomplete="off" />
      </el-form-item>
      <el-form-item v-if="!newEmployee.id" prop="password" label="Пароль">
        <el-input placeholder="***" v-model="newEmployee.password" type="password" autocomplete="off" show-password />
      </el-form-item>
      <el-form-item v-if="newEmployee.id" prop="password" label="Пароль">
        <el-input v-model="newEmployee.password" type="password" placeholder="Введите новый пароль (не обязательно)"
                  autocomplete="new-password" />
      </el-form-item>
      <el-form-item label="Телефон" prop="phone">
        <vue-tel-input
          v-model="newEmployee.phone"
          v-bind="phoneInputProps"
          :format-on-display="true"
          :mode="'international'"
          :preferred-countries="['ru', 'by', 'kz']"
          @validate="handlePhoneValidation"
        />
      </el-form-item>

      <el-form-item label="Доступ к объектам" prop="locations">
        <AccessTree
          :key="accessTreeKey"
          :tree-data="cities"
          :readonly="false"
          v-model:selectedAccess="newEmployee.locations"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="showForm = false">Отмена</el-button>
      <el-button type="primary" @click="submitForm">{{ editMode ? 'Обновить' : 'Добавить' }}</el-button>
    </template>
  </el-dialog>

  <RolesManager
    v-model="showRolesManager"
    :employees="users"
  />
</template>

<script>
import { ElMessage } from 'element-plus'
import { mapActions, mapGetters, mapState } from 'vuex'
import { formatPhone } from '../util/helpers'
import RolesManager from '@/components/page/RolesManager.vue'
import { Delete, Edit, List, Setting } from '@element-plus/icons-vue'
import store from '@/components/store/store'
import AccessTree from '@/components/util/AccessTree.vue'
import { permissionGroups, permissionLabels } from '@/components/util/accessHelper'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Employees',
  components: {
    AccessTree,
    RolesManager
  },
  computed: {
    Delete() {
      return Delete
    },
    Edit() {
      return Edit
    },
    Setting() {
      return Setting
    },
    ...mapState(['uploading', 'uploadProgress', 'uploadEmployeeId']),
    ...mapGetters('cities', ['cities']),
    ...mapGetters(['allRoles']),
    ...mapState('users', ['employees']),
    users() {
      return this.employees
    },
    paginatedUsers() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.users.slice(start, start + this.pageSize)
    },
    List() {
      return List
    }
  },
  data() {
    return {
      accessTreeKey: 0,
      expandedIds: [],
      showForm: false,
      showRolesManager: false,
      editMode: false,
      currentEmployeeId: null,
      newEmployee: {
        id: null,
        name: '',
        phone: '',
        email: '',
        role_id: null,
        locations: []
      },
      showUploadButton: null,
      isPhoneComplete: false,
      phoneInputProps: {
        inputOptions: {
          placeholder: 'Введите телефон'
        }
      },
      rules: {
        name: [
          { required: true, message: 'Введите ФИО', trigger: 'blur' }
        ],
        phone: [
          { required: true, message: 'Введите телефон', trigger: 'blur' }
        ],
        email: [
          { required: true, message: 'Введите email', trigger: 'blur' },
          { type: 'email', message: 'Неверный формат email', trigger: 'blur' },
          {
            trigger: 'blur',
            validator: async (_rule, value, callback) => {
              if (!value) return callback()

              // Если редактирование — пропускаем проверку
              if (this.editMode) return callback()

              const exists = await this.checkEmail(value)
              if (exists) callback(new Error('Email уже используется'))
              else callback()
            }
          }
        ],
        role_id: [
          { required: true, message: 'Выберите роль', trigger: 'change' }
        ],
        locations: [
          {
            validator: (rule, value, callback) => {
              value && value.length
                ? callback()
                : callback(new Error('Выберите хотя бы одну локацию'))
            },
            trigger: 'change'
          }
        ]
      },
      permissionLabels,
      permissionGroups,
      currentPage: 1,
      pageSize: 40
    }
  },
  async created() {
    await store.dispatch('users/getUsers')
    await store.dispatch('cities/getCities')
  },
  methods: {
    ...mapActions('auth', ['checkEmail']),
    getGroupedPermissions(employee) {
      if (!employee || !employee.role || !Array.isArray(employee.role.permissions)) return {}

      const result = {}
      const perms = employee.role.permissions

      for (const [groupName, groupPerms] of Object.entries(this.permissionGroups)) {
        const found = groupPerms.filter(p => perms.includes(p))
        if (found.length) result[groupName] = found
      }
      // Права вне групп
      const grouped = Object.values(this.permissionGroups).flat()
      const otherPerms = perms.filter(p => !grouped.includes(p))
      if (otherPerms.length) result['Прочее'] = otherPerms
      return result
    },
    ...mapActions('roles', ['fetchRoles', 'createRole', 'updateRole', 'deleteRole']),
    ...mapActions(['uploadAvatar']),
    async handleAvatarUpload(file, employeeId) {
      await this.$store.dispatch('uploadAvatar', {
        file,
        data: { employeeId }
      })
    },
    beforeAvatarUpload(file) {
      const isImage = file.type.startsWith('image/')
      const isLt10M = file.size / 1024 / 1024 < 10
      if (!isImage) this.$message.error('Можно загружать только изображения!')
      if (!isLt10M) this.$message.error('Размер файла не должен превышать 10 МБ!')
      return isImage && isLt10M
    },
    getInitials(fullName) {
      if (!fullName) return ''
      const parts = fullName.trim().split(' ')
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase()
    },
    toggle(id) {
      if (this.expandedIds.includes(id)) {
        this.expandedIds = this.expandedIds.filter((eid) => eid !== id)
      } else {
        this.expandedIds.push(id)
      }
    },
    openAddForm() {
      this.resetForm()
      this.showForm = true
    },
    editEmployee(employee) {
      this.newEmployee = {
        ...JSON.parse(JSON.stringify(employee)),
        phone: this.normalizePhoneForInput(employee.phone),
        password: '',
        role_id: employee.role_id,
        is_admin: employee.is_admin
      }
      this.isPhoneComplete = this.isValidPhone(employee.phone)
      this.currentEmployeeId = employee.id
      this.editMode = true
      this.showForm = true
      this.accessTreeKey++
    },
    async deleteEmployee(node) {
      const message = `Вы уверены, что хотите удалить сотрудника "${node.name}"?`
      this.$confirm(message, 'Внимание', {
        confirmButtonText: 'Р”Р°',
        cancelButtonText: 'Нет',
        type: 'warning'
      }).then(async () => {
        await this.$store.dispatch('users/deleteUser', { id: node.id })
      }).catch(() => {
        this.$message({ type: 'info', message: 'Удаление отменено' })
      })
    },
    resetForm() {
      this.$refs.employeeForm?.resetFields?.()
      this.newEmployee = {
        id: null,
        name: '',
        phone: '',
        email: '',
        role_id: null,
        locations: [],
        is_admin: false
      }
      this.accessTreeKey++ // <--- это сбросит AccessTree
      this.editMode = false
      this.currentEmployeeId = null
      this.isPhoneComplete = false
    },
    handlePhoneValidation(phoneMeta) {
      this.isPhoneComplete = Boolean(phoneMeta?.valid)
    },
    normalizePhoneForInput(phone) {
      let digits = String(phone || '').replace(/\D/g, '')
      if (digits.length === 11 && digits.startsWith('8')) {
        digits = `7${digits.slice(1)}`
      }
      return digits ? `+${digits}` : ''
    },
    normalizePhoneForSave(phone) {
      let digits = String(phone || '').replace(/\D/g, '')
      if (digits.length === 11 && digits.startsWith('8')) {
        digits = `7${digits.slice(1)}`
      }
      return digits
    },
    async submitForm() {
      await this.$refs.employeeForm.validate(async valid => {
        if (!valid) return
        if (!this.isPhoneComplete) {
          ElMessage.error('Пожалуйста, введите полный номер телефона')
          return
        }
        const payload = {
          ...this.newEmployee,
          locations: this.newEmployee.locations, // массив локаций
          phone: parseInt(this.normalizePhoneForSave(this.newEmployee.phone), 10)
        }
        try {
          if (this.editMode) {
            await this.$store.dispatch('users/updateUser', payload)
            ElMessage.success('Сотрудник обновлен')
          } else {
            await this.$store.dispatch('users/createUser', payload)
            ElMessage.success('Сотрудник добавлен')
          }
          this.resetForm()
          this.showForm = false
        } catch (error) {
          ElMessage.error('Ошибка сохранения: ' + error.message)
        }
      })
    },
    isValidPhone(phone) {
      const cleaned = ('' + phone).replace(/\D/g, '')
      return cleaned.length === 11
    },
    handlePageChange(page) {
      this.currentPage = page
    },
    formatPhone,
  }
}
</script>

<style scoped>
.permissions-list.grouped {
  list-style: none;
  padding: 0;
  margin: 0;
}

.permissions-list.grouped li {
  margin-bottom: 6px;
}

.permission-label {
  display: inline-block;
  background: rgba(255, 255, 255, 0.06);
  color: #d8e8ff;
  border-radius: 999px;
  padding: 4px 10px;
  margin-right: 4px;
  font-size: 12px;
  margin-bottom: 2px;
}

.employees-page {
  background:
    radial-gradient(circle at top left, rgba(22, 61, 124, 0.28), transparent 34%),
    linear-gradient(180deg, #091325 0%, #07111f 100%);
  padding: 24px 18px 28px;
}

.employees-page h3 {
  margin-bottom: 18px;
  color: #f5f9ff;
  font-family: var(--ui-font-family-base);
  font-size: 32px;
  font-weight: 500;
}

.employees {
  margin: 0 auto;
  font-family: var(--ui-font-family-base);
}

.employees-panel {
  padding: 18px 18px 20px;
  border-radius: 22px;
  border: 1px solid rgba(110, 146, 214, 0.18);
  background: linear-gradient(180deg, rgba(10, 26, 52, 0.96), rgba(7, 18, 36, 0.94));
  box-shadow: 0 24px 48px rgba(2, 8, 18, 0.34);
}

.employees-toolbar {
  gap: 12px;
}

.employees-toolbar :deep(.el-button) {
  min-height: 42px;
  border-radius: 14px;
}

.employees-toolbar :deep(.el-button--primary) {
  border: none;
  background: linear-gradient(135deg, #22c0ff, #2b7cff);
  box-shadow: 0 16px 32px rgba(0, 123, 255, 0.2);
}

.employees-toolbar :deep(.el-button:not(.el-button--primary)) {
  background: linear-gradient(180deg, rgba(13, 30, 58, 0.96), rgba(7, 18, 36, 0.96));
  border-color: rgba(110, 146, 214, 0.18);
  color: #d7e7ff;
}

.employee-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.employee-item {
  border: 1px solid var(--ui-color-border-muted);
  border-radius: 6px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.employee-item:hover {
  box-shadow: var(--ui-shadow-soft);
}

.employees-empty {
  color: var(--ui-color-text-muted);
}

.employee-header {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 12px 16px;
  background-color: var(--ui-color-bg-base);
  user-select: none;
}

.employee-info {
  margin-left: 16px;
  flex-grow: 1;
}

.employee-info h3 {
  margin: 0;
  font-size: 18px;
}

.employee-info p {
  margin: 4px 0 0;
  color: #666;
  font-size: 14px;
}

.toggle-btn {
  background: none;
  border: none;
  font-size: 18px;
  user-select: none;
  cursor: pointer;
  color: var(--ui-color-text-soft);
  padding: 0 8px;
}

.employee-details {
  position: relative;
  padding: 16px 16px 16px 40px;
  background-color: var(--ui-color-white);
  border-top: 1px solid var(--ui-color-border-muted);
  font-size: 14px;
  color: var(--ui-color-text-strong);
  min-height: 80px;
  text-align: start;
}

.action-buttons {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 8px;
}

.el-icon {
  vertical-align: middle;
}

.avatar-container {
  position: relative;
  display: inline-block;
}

.avatar-hover-container {
  position: relative;
  display: inline-block;
  cursor: pointer;
  border-radius: 50%;
  overflow: hidden;
}

.avatar-hover-mask {
  height: 50px;
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: var(--ui-color-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  border-radius: 50%;
  z-index: 1;
}

.avatar-hover-container:hover .avatar-hover-mask {
  opacity: 1;
  pointer-events: auto;
}

.avatar-hover-icon {
  color: var(--ui-color-white);
  font-size: 22px;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.fade-enter-to, .fade-leave-from {
  opacity: 1;
}

.pagination {
  margin-top: 20px;
  text-align: center;
}

.scroll-wrapper {
  height: 100%;
  overflow-y: auto;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
}

/* Фикс: предотвращаем перенос строк в ячейках таблицы сотрудников */
.el-table th,
.el-table td {
  white-space: nowrap;
}

.nowrap-ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  max-width: 100%;
}

.employees-page :deep(.el-table),
.employees-page :deep(.el-table__expanded-cell),
.employees-page :deep(.el-table tr),
.employees-page :deep(.el-table th.el-table__cell),
.employees-page :deep(.el-table td.el-table__cell) {
  background: #08172f !important;
  color: #d7e7ff !important;
  border-bottom-color: rgba(174, 194, 230, 0.1) !important;
}

.employees-page :deep(.el-table) {
  --el-table-border-color: rgba(174, 194, 230, 0.1);
  --el-table-header-bg-color: #0e2447;
  --el-table-row-hover-bg-color: rgba(0, 195, 254, 0.08);
  border: 1px solid rgba(110, 146, 214, 0.12);
  border-radius: 18px;
  overflow: hidden;
}

.employees-page :deep(.el-table th.el-table__cell) {
  color: rgba(174, 194, 230, 0.72) !important;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
}

.employees-page :deep(.el-link) {
  color: #22c0ff !important;
}

.employees-page :deep(.el-table .el-button.is-circle) {
  width: 34px !important;
  height: 34px !important;
  min-width: 34px !important;
  background: #1a3b7c !important;
  border: none !important;
  box-shadow: none !important;
}

.employees-page :deep(.el-table .el-button.is-circle .el-icon) {
  color: #031230 !important;
}

.employees-page :deep(.el-table .el-button.is-circle .el-icon svg),
.employees-page :deep(.el-table .el-button.is-circle .el-icon svg path) {
  color: #031230 !important;
  fill: #031230 !important;
  stroke: #031230 !important;
}

.employees-page :deep(.el-table .el-button.is-circle.el-button--danger) {
  background: #000714 !important;
}

.employees-page :deep(.el-table .el-button.is-circle.el-button--danger .el-icon) {
  color: #2d5096 !important;
}

.employees-page :deep(.el-table .el-button.is-circle.el-button--danger .el-icon svg),
.employees-page :deep(.el-table .el-button.is-circle.el-button--danger .el-icon svg path) {
  color: #2d5096 !important;
  fill: #2d5096 !important;
  stroke: #2d5096 !important;
}

.employees-page :deep(.el-table .el-button.is-circle:hover) {
  filter: none !important;
}

.employees-page :deep(.el-pagination.is-background .btn-next),
.employees-page :deep(.el-pagination.is-background .btn-prev),
.employees-page :deep(.el-pagination.is-background .el-pager li) {
  background: rgba(12, 34, 75, 0.92);
  color: #d7e7ff;
}

.employees-page :deep(.el-pagination.is-background .el-pager li.is-active) {
  background: linear-gradient(135deg, #22c0ff, #2b7cff);
  color: #081221;
}

.employees-dialog.el-dialog {
  width: min(720px, calc(100vw - 24px)) !important;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid rgba(110, 146, 214, 0.2);
  background: linear-gradient(180deg, rgba(12, 28, 55, 0.98), rgba(7, 18, 36, 0.98));
}

.employees-dialog :deep(.el-dialog__header),
.employees-dialog :deep(.el-dialog__body),
.employees-dialog :deep(.el-dialog__footer) {
  background: transparent;
}

.employees-dialog :deep(.el-dialog__title),
.employees-dialog :deep(.el-form-item__label) {
  color: #eef5ff;
}

.employees-dialog :deep(.el-input__wrapper),
.employees-dialog :deep(.el-textarea__inner),
.employees-dialog :deep(.el-select__wrapper),
.employees-dialog :deep(.vue-tel-input) {
  background: rgba(10, 26, 52, 0.96) !important;
  box-shadow: inset 0 0 0 1px rgba(110, 146, 214, 0.16) !important;
  color: #eef5ff !important;
}

.employees-dialog :deep(.el-input__inner),
.employees-dialog :deep(.el-textarea__inner),
.employees-dialog :deep(.el-select__placeholder),
.employees-dialog :deep(.vti__input) {
  color: #eef5ff !important;
}

.employees-dialog :deep(.vue-tel-input) {
  border: none;
}

.employees-dialog :deep(.vti__dropdown) {
  background: rgba(10, 26, 52, 0.96);
}

.employees-dialog :deep(.vti__input),
.employees-dialog :deep(.vti__selection) {
  background: transparent !important;
  color: #eef5ff !important;
}

.employees-dialog :deep(.el-button--primary) {
  border: none;
  background: linear-gradient(135deg, #22c0ff, #2b7cff);
}

.employees-dialog :deep(.el-button:not(.el-button--primary)) {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(110, 146, 214, 0.18);
  color: #d7e7ff;
}

@media (max-width: 768px) {
  .employees-page {
    padding: 16px 12px 22px;
  }

  .employees-page h3 {
    font-size: 28px;
  }

  .employees-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .employees-panel {
    padding: 14px;
    border-radius: 18px;
  }
}

@media (max-width: 640px) {
  .employees-dialog.el-dialog {
    width: calc(100vw - 16px) !important;
    margin-top: 4vh !important;
  }
}

</style>

/* Flex utility classes for Employees.vue (if Bootstrap is not used) */
.d-flex {
  display: flex;
}
.justify-content-between {
  justify-content: space-between;
}
.align-items-center {
  align-items: center;
}
.mb-3 {
  margin-bottom: 1rem;
}

